import React from 'react';
import { Award, Check, Lock } from 'lucide-react';
import { BADGE_ICONS, RARITY_STYLES } from './constants';

const BadgesTab = ({ achievements, unlockedBadges, lockedBadges }) => (
  <div className="space-y-6">
    {/* Summary banner */}
    <div className="glass-card p-5 border border-[#FFD700]/30" data-testid="badges-summary">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FF9500 100%)' }}>
            <Award className="w-6 h-6 text-black" />
          </div>
          <div>
            <h2 className="font-game text-base text-[#FFD700]">ACHIEVEMENT BADGES</h2>
            <p className="text-xs text-[#A1A1AA]">
              {achievements ? `${achievements.badge_count} of ${achievements.total_badges} unlocked` : 'Loading...'}
            </p>
          </div>
        </div>
        {achievements && achievements.total_badges > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-32 sm:w-48 h-2.5 rounded-full bg-[#1a1a1a] overflow-hidden">
              <div className="h-full transition-all"
                style={{
                  width: `${(achievements.badge_count / achievements.total_badges) * 100}%`,
                  background: 'linear-gradient(90deg, #14F195 0%, #FFD700 100%)'
                }} />
            </div>
            <span className="font-game text-xs text-[#FFD700]">
              {Math.round((achievements.badge_count / achievements.total_badges) * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>

    {/* Unlocked Badges */}
    <div className="glass-card p-5">
      <h3 className="font-game text-sm text-[#14F195] mb-4 flex items-center gap-2">
        <Check className="w-4 h-4" /> UNLOCKED ({unlockedBadges.length})
      </h3>
      {!achievements ? (
        <p className="text-[#666] text-sm text-center py-4">Loading badges...</p>
      ) : unlockedBadges.length === 0 ? (
        <p className="text-[#666] text-sm text-center py-6">
          No badges unlocked yet. Play games and win tournaments to earn them!
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {unlockedBadges.map(b => {
            const Icon = BADGE_ICONS[b.icon] || Award;
            const rarity = RARITY_STYLES[b.rarity] || RARITY_STYLES.common;
            return (
              <div key={b.id}
                className="relative bg-[#0a0a0a] rounded-xl p-4 border-2 transition-transform hover:scale-105"
                style={{
                  borderColor: rarity.color,
                  boxShadow: `0 0 18px ${rarity.glow}`
                }}
                data-testid={`badge-unlocked-${b.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${rarity.color}25`,
                      border: `1px solid ${rarity.color}`
                    }}>
                    <Icon className="w-6 h-6" style={{ color: b.color || rarity.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-game text-sm text-white truncate">{b.name}</p>
                    <p className="text-[9px] uppercase tracking-wider font-game"
                      style={{ color: rarity.color }}>{rarity.label}</p>
                  </div>
                </div>
                <p className="text-[10px] text-[#A1A1AA] leading-relaxed">{b.description}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Locked / In-progress Badges */}
    {achievements && lockedBadges.length > 0 && (
      <div className="glass-card p-5">
        <h3 className="font-game text-sm text-[#666] mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4" /> IN PROGRESS ({lockedBadges.length})
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {lockedBadges.map(b => {
            const Icon = BADGE_ICONS[b.icon] || Award;
            const rarity = RARITY_STYLES[b.rarity] || RARITY_STYLES.common;
            const pct = b.requirement > 0
              ? Math.min(100, Math.round((b.progress / b.requirement) * 100))
              : 0;
            return (
              <div key={b.id}
                className="relative bg-[#0a0a0a] rounded-xl p-4 border border-[#222] opacity-70"
                data-testid={`badge-locked-${b.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[#111] border border-[#222] flex-shrink-0 relative">
                    <Icon className="w-5 h-5 text-[#444]" />
                    <Lock className="w-3 h-3 text-[#666] absolute bottom-1 right-1" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-game text-xs text-[#888] truncate">{b.name}</p>
                    <p className="text-[9px] uppercase tracking-wider font-game text-[#444]">{rarity.label}</p>
                  </div>
                </div>
                <p className="text-[10px] text-[#666] mb-2 leading-relaxed">{b.description}</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-[#1a1a1a] overflow-hidden">
                    <div className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: rarity.color }} />
                  </div>
                  <span className="text-[9px] font-game text-[#888]">
                    {b.progress?.toLocaleString()}/{b.requirement?.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

export default BadgesTab;
